import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import "./Loader.css";
import { CapabilitiesProvider, useCapabilities } from "./contexts/CapabilitiesContext";
import Attachment from "./Attachment";
import ChangePassword from "./ChangePassword";
import ConfirmPopup from "./ConfirmPopup";
import CustodyLog from "./CustodyLog";
import Dashbord from "./Dashbord";
import DataList from "./DataList";
import DataView from "./DataView";
import DeletePopup from "./DeletePopup";
import ForgotPassword from "./ForgotPassword";
import InsertData from "./InsertData";
import NoAccessPage from "./NoAccessPage";
import ReportPage from "./ReportPage";
import SignIn from "./SignIn";
import SignUp from "./SignUp";
import ViewDate from "./ViewDate";
import { MenuProvider, useMenu } from "./contexts/MenuContext";
import { clearStoredMenuItems, NO_ACCESS_ROUTE } from "./services/menuService";
import DistrictAdd from "./masters/districts/DistrictAdd";
import DistrictEdit from "./masters/districts/DistrictEdit";
import DistrictList from "./masters/districts/DistrictList";
import DistrictView from "./masters/districts/DistrictView";
import LogsPage from "./masters/logs/LogsPage";
import LawReferencePage from "./masters/references/LawReferencePage";
import RoleAccessPage from "./masters/role-access/RoleAccessPage";
import StationAdd from "./masters/stations/StationAdd";
import StationEdit from "./masters/stations/StationEdit";
import StationList from "./masters/stations/StationList";
import StationView from "./masters/stations/StationView";
import SubDivisionAdd from "./masters/sub-divisions/SubDivisionAdd";
import SubDivisionEdit from "./masters/sub-divisions/SubDivisionEdit";
import SubDivisionList from "./masters/sub-divisions/SubDivisionList";
import SubDivisionView from "./masters/sub-divisions/SubDivisionView";
import UserAdd from "./masters/users/UserAdd";
import UserEdit from "./masters/users/UserEdit";
import UserList from "./masters/users/UserList";
import UserView from "./masters/users/UserView";

const AUTH_STATE_CHANGE_EVENT = "auth-state-changed";

const LoaderView = () => (
  <div className="loader-container">
    <div className="loader"></div>
  </div>
);

const CASE_PROPERTY_ROUTE_PATHS = ["/records", "/datalist"];
const DEFAULT_POST_LOGIN_ROUTE = "/datalist";
const DEFAULT_FALLBACK_ROUTE = "/dashboard";

const resolveHomeRoute = (candidatePath) => {
  if (candidatePath && candidatePath !== NO_ACCESS_ROUTE) {
    return candidatePath;
  }

  return DEFAULT_FALLBACK_ROUTE;
};

const getDeniedRouteFallback = (guardedPaths, defaultMenuPath) => {
  const resolvedDefaultPath = resolveHomeRoute(defaultMenuPath);

  if (resolvedDefaultPath && !guardedPaths.includes(resolvedDefaultPath)) {
    return resolvedDefaultPath;
  }

  if (
    guardedPaths.includes("/records") ||
    guardedPaths.includes("/datalist")
  ) {
    return DEFAULT_FALLBACK_ROUTE;
  }

  return DEFAULT_POST_LOGIN_ROUTE;
};

const LegacyRouteRedirect = ({ buildPath }) => {
  const params = useParams();
  return <Navigate replace to={buildPath(params)} />;
};

const MenuHomeRedirect = () => {
  const { getDefaultMenuPath, menuLoading } = useMenu();

  if (menuLoading) {
    return <LoaderView />;
  }

  const defaultMenuPath = resolveHomeRoute(getDefaultMenuPath());
  return <Navigate replace to={defaultMenuPath || DEFAULT_FALLBACK_ROUTE} />;
};

const ProtectedRoute = ({
  element,
  requiredPath,
  requiredPaths,
  requiredPermission,
}) => {
  const { getDefaultMenuPath, hasMenuAccess, menuLoading } = useMenu();
  const { can, canAny, loading: capabilitiesLoading } = useCapabilities();
  const guardedPaths = Array.isArray(requiredPaths)
    ? requiredPaths
    : requiredPath
    ? [requiredPath]
    : [];

  if (menuLoading || capabilitiesLoading) {
    return <LoaderView />;
  }

  if (guardedPaths.length > 0 && !guardedPaths.some((path) => hasMenuAccess(path))) {
    return (
      <Navigate
        replace
        to={getDeniedRouteFallback(guardedPaths, getDefaultMenuPath())}
      />
    );
  }

  if (
    requiredPermission?.resource &&
    requiredPermission?.action &&
    !(
      Array.isArray(requiredPermission.action)
        ? canAny(requiredPermission.resource, requiredPermission.action)
        : can(requiredPermission.resource, requiredPermission.action)
    )
  ) {
    return <Navigate replace to={resolveHomeRoute(getDefaultMenuPath())} />;
  }

  return element;
};

const AppRoutes = ({ handleLogin, handleLogout, loggedIn, userName }) => {
  const { menuError } = useMenu();
  const isAuthenticated = Boolean(
    localStorage.getItem("authKey") && localStorage.getItem("userName")
  );

  return (
    <Routes>
      <Route
        path="/"
        element={isAuthenticated ? <MenuHomeRedirect /> : <SignIn onLogin={handleLogin} />}
      />
      <Route
        path="/login"
        element={isAuthenticated ? <MenuHomeRedirect /> : <SignIn onLogin={handleLogin} />}
      />
      <Route
        path="/signup"
        element={isAuthenticated ? <MenuHomeRedirect /> : <SignUp onSignUp={() => undefined} />}
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {isAuthenticated ? (
        <>
          <Route
            path={NO_ACCESS_ROUTE}
            element={<NoAccessPage allowRetry={Boolean(menuError)} />}
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute
                element={<Dashbord onLogout={handleLogout} />}
                requiredPath="/dashboard"
              />
            }
          />
          <Route path="/dashbord" element={<Navigate replace to="/dashboard" />} />
          <Route
            path="/records"
            element={
              <ProtectedRoute
                element={<DataList onLogout={handleLogout} userName={userName} />}
                requiredPaths={CASE_PROPERTY_ROUTE_PATHS}
                requiredPermission={{ resource: "crime_data", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/datalist"
            element={
              <ProtectedRoute
                element={<DataList onLogout={handleLogout} userName={userName} />}
                requiredPaths={CASE_PROPERTY_ROUTE_PATHS}
                requiredPermission={{ resource: "crime_data", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/insertdata"
            element={
              <ProtectedRoute
                element={<InsertData onLogout={handleLogout} />}
                requiredPaths={CASE_PROPERTY_ROUTE_PATHS}
                requiredPermission={{ resource: "crime_data", action: ["create", "manage"] }}
              />
            }
          />
          <Route
            path="/attachment/:id"
            element={
              <ProtectedRoute
                element={<Attachment />}
                requiredPaths={CASE_PROPERTY_ROUTE_PATHS}
                requiredPermission={{ resource: "crime_data", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/dataview/:id"
            element={
              <ProtectedRoute
                element={<DataView />}
                requiredPaths={CASE_PROPERTY_ROUTE_PATHS}
                requiredPermission={{ resource: "crime_data", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/deletepopup/:id"
            element={
              <ProtectedRoute
                element={<DeletePopup />}
                requiredPaths={CASE_PROPERTY_ROUTE_PATHS}
              />
            }
          />
          <Route
            path="/confirmpopup:id"
            element={
              <ProtectedRoute
                element={<ConfirmPopup />}
                requiredPaths={CASE_PROPERTY_ROUTE_PATHS}
              />
            }
          />
          <Route
            path="/custody-log"
            element={
              <ProtectedRoute
                element={
                  <NoAccessPage
                    description="Open custody log entries from the records module until a standalone custody-log list page is available."
                    title="Custody Log"
                  />
                }
                requiredPath="/custody-log"
              />
            }
          />
          <Route
            path="/new-screen/:id"
            element={
              <ProtectedRoute
                element={<CustodyLog />}
                requiredPaths={["/reports", ...CASE_PROPERTY_ROUTE_PATHS]}
              />
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute
                element={<Dashbord onLogout={handleLogout} />}
                requiredPath="/reports"
              />
            }
          />
          <Route
            path="/report-page/:categoryType/:categoryName"
            element={
              <ProtectedRoute
                element={<ReportPage />}
                requiredPath="/reports"
                requiredPermission={{ resource: "reports", action: ["read_admin", "read"] }}
              />
            }
          />
          <Route
            path="/report-page/:categoryType/change-password"
            element={<ChangePassword />}
          />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route
            path="/View-Date"
            element={
              <ProtectedRoute
                element={<ViewDate />}
                requiredPath="/View-Date"
              />
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute
                element={<UserList />}
                requiredPath="/users"
                requiredPermission={{ resource: "users", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/users/add"
            element={
              <ProtectedRoute
                element={<UserAdd />}
                requiredPath="/users"
                requiredPermission={{ resource: "users", action: ["create", "manage"] }}
              />
            }
          />
          <Route
            path="/users/:id/view"
            element={
              <ProtectedRoute
                element={<UserView />}
                requiredPath="/users"
                requiredPermission={{ resource: "users", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/users/:id/edit"
            element={
              <ProtectedRoute
                element={<UserEdit />}
                requiredPath="/users"
                requiredPermission={{ resource: "users", action: ["update", "manage"] }}
              />
            }
          />
          <Route
            path="/districts"
            element={
              <ProtectedRoute
                element={<DistrictList />}
                requiredPath="/districts"
                requiredPermission={{ resource: "districts", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/districts/add"
            element={
              <ProtectedRoute
                element={<DistrictAdd />}
                requiredPath="/districts"
                requiredPermission={{ resource: "districts", action: ["create", "manage"] }}
              />
            }
          />
          <Route
            path="/districts/:id/view"
            element={
              <ProtectedRoute
                element={<DistrictView />}
                requiredPath="/districts"
                requiredPermission={{ resource: "districts", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/districts/:id/edit"
            element={
              <ProtectedRoute
                element={<DistrictEdit />}
                requiredPath="/districts"
                requiredPermission={{ resource: "districts", action: ["update", "manage"] }}
              />
            }
          />
          <Route
            path="/sub-divisions"
            element={
              <ProtectedRoute
                element={<SubDivisionList />}
                requiredPath="/sub-divisions"
                requiredPermission={{ resource: "sub_divisions", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/sub-divisions/add"
            element={
              <ProtectedRoute
                element={<SubDivisionAdd />}
                requiredPath="/sub-divisions"
                requiredPermission={{ resource: "sub_divisions", action: ["create", "manage"] }}
              />
            }
          />
          <Route
            path="/sub-divisions/:id/view"
            element={
              <ProtectedRoute
                element={<SubDivisionView />}
                requiredPath="/sub-divisions"
                requiredPermission={{ resource: "sub_divisions", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/sub-divisions/:id/edit"
            element={
              <ProtectedRoute
                element={<SubDivisionEdit />}
                requiredPath="/sub-divisions"
                requiredPermission={{ resource: "sub_divisions", action: ["update", "manage"] }}
              />
            }
          />
          <Route
            path="/stations"
            element={
              <ProtectedRoute
                element={<StationList />}
                requiredPath="/stations"
                requiredPermission={{ resource: "stations", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/stations/add"
            element={
              <ProtectedRoute
                element={<StationAdd />}
                requiredPath="/stations"
                requiredPermission={{ resource: "stations", action: ["create", "manage"] }}
              />
            }
          />
          <Route
            path="/stations/:id/view"
            element={
              <ProtectedRoute
                element={<StationView />}
                requiredPath="/stations"
                requiredPermission={{ resource: "stations", action: ["read", "manage"] }}
              />
            }
          />
          <Route
            path="/stations/:id/edit"
            element={
              <ProtectedRoute
                element={<StationEdit />}
                requiredPath="/stations"
                requiredPermission={{ resource: "stations", action: ["update", "manage"] }}
              />
            }
          />
          <Route
            path="/masters"
            element={
              <ProtectedRoute
                element={<LawReferencePage />}
                requiredPath="/masters"
                requiredPermission={{ resource: "masters", action: ["read", "manage"] }}
              />
            }
          />
          <Route path="/law-reference" element={<Navigate replace to="/masters" />} />
          <Route
            path="/payments"
            element={
              <ProtectedRoute
                element={
                  <NoAccessPage
                    description="The Payments module route is reserved, but a standalone page is not yet available in this frontend build."
                    title="Payments"
                  />
                }
                requiredPath="/payments"
              />
            }
          />
          <Route
            path="/role-access"
            element={
              <ProtectedRoute
                element={<RoleAccessPage />}
                requiredPath="/role-access"
              />
            }
          />
          <Route
            path="/logs"
            element={
              <ProtectedRoute
                element={<LogsPage />}
                requiredPath="/logs"
              />
            }
          />
          <Route path="/masters/districts" element={<Navigate replace to="/districts" />} />
          <Route path="/masters/districts/add" element={<Navigate replace to="/districts/add" />} />
          <Route
            path="/masters/districts/:id/view"
            element={
              <LegacyRouteRedirect
                buildPath={({ id }) => `/districts/${id}/view`}
              />
            }
          />
          <Route
            path="/masters/districts/:id/edit"
            element={
              <LegacyRouteRedirect
                buildPath={({ id }) => `/districts/${id}/edit`}
              />
            }
          />
          <Route path="/masters/sub-divisions" element={<Navigate replace to="/sub-divisions" />} />
          <Route path="/masters/sub-divisions/add" element={<Navigate replace to="/sub-divisions/add" />} />
          <Route
            path="/masters/sub-divisions/:id/view"
            element={
              <LegacyRouteRedirect
                buildPath={({ id }) => `/sub-divisions/${id}/view`}
              />
            }
          />
          <Route
            path="/masters/sub-divisions/:id/edit"
            element={
              <LegacyRouteRedirect
                buildPath={({ id }) => `/sub-divisions/${id}/edit`}
              />
            }
          />
          <Route path="/masters/stations" element={<Navigate replace to="/stations" />} />
          <Route path="/masters/stations/add" element={<Navigate replace to="/stations/add" />} />
          <Route
            path="/masters/stations/:id/view"
            element={
              <LegacyRouteRedirect
                buildPath={({ id }) => `/stations/${id}/view`}
              />
            }
          />
          <Route
            path="/masters/stations/:id/edit"
            element={
              <LegacyRouteRedirect
                buildPath={({ id }) => `/stations/${id}/edit`}
              />
            }
          />
          <Route path="/masters/users" element={<Navigate replace to="/users" />} />
          <Route path="/masters/users/add" element={<Navigate replace to="/users/add" />} />
          <Route
            path="/masters/users/:id/view"
            element={
              <LegacyRouteRedirect
                buildPath={({ id }) => `/users/${id}/view`}
              />
            }
          />
          <Route
            path="/masters/users/:id/edit"
            element={
              <LegacyRouteRedirect
                buildPath={({ id }) => `/users/${id}/edit`}
              />
            }
          />
          <Route path="/masters/role-access" element={<Navigate replace to="/role-access" />} />
          <Route path="/masters/logs" element={<Navigate replace to="/logs" />} />
        </>
      ) : (
        <>
          <Route path="/dashboard" element={<Navigate replace to="/login" />} />
          <Route path="/dashbord" element={<Navigate replace to="/login" />} />
          <Route path="/records/*" element={<Navigate replace to="/login" />} />
          <Route path="/datalist/*" element={<Navigate replace to="/login" />} />
          <Route path="/custody-log/*" element={<Navigate replace to="/login" />} />
          <Route path="/reports/*" element={<Navigate replace to="/login" />} />
          <Route path="/districts/*" element={<Navigate replace to="/login" />} />
          <Route path="/sub-divisions/*" element={<Navigate replace to="/login" />} />
          <Route path="/stations/*" element={<Navigate replace to="/login" />} />
          <Route path="/users/*" element={<Navigate replace to="/login" />} />
          <Route path="/law-reference/*" element={<Navigate replace to="/login" />} />
          <Route path="/payments/*" element={<Navigate replace to="/login" />} />
          <Route path="/role-access/*" element={<Navigate replace to="/login" />} />
          <Route path="/logs/*" element={<Navigate replace to="/login" />} />
          <Route path="/View-Date" element={<Navigate replace to="/login" />} />
          <Route path="/new-screen/:id" element={<Navigate replace to="/login" />} />
          <Route path="/attachment/:id" element={<Navigate replace to="/login" />} />
          <Route path="/report-page/:categoryType/:categoryName" element={<Navigate replace to="/login" />} />
          <Route path="/dataview/:id" element={<Navigate replace to="/login" />} />
          <Route path="/change-password" element={<Navigate replace to="/login" />} />
          <Route path="/deletepopup/:id" element={<Navigate replace to="/login" />} />
          <Route path="/confirmpopup:id" element={<Navigate replace to="/login" />} />
          <Route path="/masters/*" element={<Navigate replace to="/login" />} />
          <Route path={NO_ACCESS_ROUTE} element={<Navigate replace to="/login" />} />
        </>
      )}

      <Route
        path="*"
        element={isAuthenticated ? <MenuHomeRedirect /> : <Navigate replace to="/login" />}
      />
    </Routes>
  );
};

export default function App() {
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");

  useEffect(() => {
    const syncAuthState = () => {
      const authKey = localStorage.getItem("authKey");
      const name = localStorage.getItem("userName");

      if (authKey && name) {
        setLoggedIn(true);
        setUserName(name);
      } else {
        setLoggedIn(false);
        setUserName("");
      }
    };

    syncAuthState();

    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 1000);

    window.addEventListener("storage", syncAuthState);
    window.addEventListener(AUTH_STATE_CHANGE_EVENT, syncAuthState);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("storage", syncAuthState);
      window.removeEventListener(AUTH_STATE_CHANGE_EVENT, syncAuthState);
    };
  }, []);

  const handleLogin = (authKey, name) => {
    if (authKey && name) {
      localStorage.setItem("authKey", authKey);
      localStorage.setItem("userName", name);
      setLoggedIn(true);
      setUserName(name);
      window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authKey");
    localStorage.removeItem("userName");
    clearStoredMenuItems();
    setLoggedIn(false);
    setUserName("");
    window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
  };

  if (loading) {
    return <LoaderView />;
  }

  return (
    <Router>
      <MenuProvider
        key={`menu-${
          loggedIn ? localStorage.getItem("authKey") || "authenticated" : "anonymous"
        }`}
        sessionKey={loggedIn ? localStorage.getItem("authKey") || "authenticated" : ""}
      >
        <CapabilitiesProvider
          key={`cap-${
            loggedIn ? localStorage.getItem("authKey") || "authenticated" : "anonymous"
          }`}
          sessionKey={loggedIn ? localStorage.getItem("authKey") || "authenticated" : ""}
        >
          <AppRoutes
            handleLogin={handleLogin}
            handleLogout={handleLogout}
            loggedIn={loggedIn}
            userName={userName}
          />
        </CapabilitiesProvider>
      </MenuProvider>
    </Router>
  );
}
